// src/pages/UnitMaster.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
} from 'reactstrap';
import {
    Plus,
    Edit,
    Trash2,
    RefreshCw,
    Eye,
    CheckCircle,
    XCircle,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// Types
interface Unit {
    id: number;
    name: string;
    short_name?: string;
    location?: string;
    description?: string;
    contact_person?: string;
    contact_phone?: string;
    status: boolean;
    created_at: string;
    updated_at: string;
}

const UnitMaster: React.FC = () => {
    // State
    const [units, setUnits] = useState<Unit[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    // Modal states
    const [modal, setModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        short_name: '',
        description: '',
        location: '',
        contact_person: '',
        contact_phone: '',
        status: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete modal
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);

    // View modal
    const [viewModal, setViewModal] = useState(false);
    const [viewingUnit, setViewingUnit] = useState<Unit | null>(null);

    // ─── Fetch companies ───
    const fetchUnits = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiClient.get(
                `/units?include_inactive=${showInactive}`,
            );
            const data = response.data as Unit[];
            setUnits(data || []);
        } catch (error: any) {
            console.error('Error fetching companies:', error);
            setError(error.message || 'Failed to fetch companies');
            toast.error('Failed to load companies');
        } finally {
            setLoading(false);
        }
    }, [showInactive]);

    useEffect(() => {
        fetchUnits();
    }, [fetchUnits]);

    // ─── Form input handler ───
    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // ─── Open add modal ───
    const openAddModal = () => {
        setModalMode('add');
        setFormData({
            name: '',
            short_name: '',
            description: '',
            contact_person: '',
            location: '',
            contact_phone: '',
            status: true,
        });
        setFormError('');
        setModal(true);
    };

    // ─── Open edit modal ───
    const openEditModal = (unit: Unit) => {
        setModalMode('edit');
        setSelectedUnit(unit);
        setFormData({
            name: unit.name,
            short_name: unit.short_name || '',
            description: unit.description || '',
            contact_person: unit.contact_person || '',
            location: unit.location || '',
            contact_phone: unit.contact_phone || '',
            status: unit.status,
        });
        setFormError('');
        setModal(true);
    };

    // ─── Close modal ───
    const closeModal = () => {
        setModal(false);
        setSelectedUnit(null);
        setFormError('');
    };

    // ─── Submit ───
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setFormError('Company name is required');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            const payload = {
                name: formData.name.trim(),
                short_name: formData.short_name.trim() || undefined,
                location: formData.location.trim() || undefined,
                description: formData.description.trim() || undefined,
                contact_person: formData.contact_person.trim() || undefined,
                contact_phone: formData.contact_phone.trim() || undefined,
                status: formData.status,
            };

            if (modalMode === 'add') {
                await apiClient.post('/units', payload);
                toast.success('Company created successfully!');
            } else {
                await apiClient.put(`/units/${selectedUnit?.id}`, payload);
                toast.success('Company updated successfully!');
            }

            closeModal();
            fetchUnits();
        } catch (error: any) {
            console.error('Error saving company:', error);
            setFormError(error.message || 'Failed to save company');
            toast.error('Failed to save company');
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Delete / Restore ───
    const openDeleteModal = (unit: Unit) => {
        setUnitToDelete(unit);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!unitToDelete) return;

        setDeleting(true);
        try {
            await apiClient.delete(`/units/${unitToDelete.id}`);
            toast.success('Company deleted successfully!');
            setDeleteModal(false);
            fetchUnits();
        } catch (error: any) {
            console.error('Error deleting company:', error);
            toast.error(error.message || 'Failed to delete company');
        } finally {
            setDeleting(false);
            setUnitToDelete(null);
        }
    };

    const handleRestore = async (unit: Unit) => {
        try {
            await apiClient.patch(`/units/${unit.id}/restore`);
            toast.success('Company restored successfully!');
            fetchUnits();
        } catch (error: any) {
            console.error('Error restoring company:', error);
            toast.error(error.message || 'Failed to restore company');
        }
    };

    // ─── View ───
    const handleView = (unit: Unit) => {
        setViewingUnit(unit);
        setViewModal(true);
    };

    // ─── Helpers ───
    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd MMM yyyy HH:mm');
        } catch {
            return 'N/A';
        }
    };

    const getStatusBadge = (status: boolean) =>
        status ? (
            <Badge color="success">
                <CheckCircle size={12} className="me-1" />
                Active
            </Badge>
        ) : (
            <Badge color="warning">
                <XCircle size={12} className="me-1" />
                Inactive
            </Badge>
        );

    // ─── Table columns ───
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => <strong>{cell.row.index + 1}</strong>,
            },
            {
                header: 'Short Code',
                accessorKey: 'short_name',
                cell: (cell: any) => {
                    const shortName = cell.getValue();
                    return shortName ? (
                        <span
                            style={{
                                backgroundColor: '#4b1ac9',
                                color: '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                padding: '3px 8px',
                                borderRadius: 4,
                                letterSpacing: '0.05em',
                            }}
                        >
                            {shortName}
                        </span>
                    ) : (
                        <span className="text-muted">—</span>
                    );
                },
            },
            {
                header: 'Company Name',
                accessorKey: 'name',
                cell: (cell: any) => (
                    <span className="fw-semibold">{cell.getValue()}</span>
                ),
            },
            {
                header: 'Location',
                accessorKey: 'location',
                cell: (cell: any) => {
                    const loc = cell.getValue();
                    return loc ? (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                color: '#334155',
                                fontSize: '0.8rem',
                            }}
                        >
                            <span style={{ color: '#dc2626' }}>📍</span>
                            {loc}
                        </span>
                    ) : (
                        <span className="text-muted">—</span>
                    );
                },
            },
            {
                header: 'Description',
                accessorKey: 'description',
                cell: (cell: any) => cell.getValue() || '—',
            },
            {
                header: 'Contact Person',
                accessorKey: 'contact_person',
                cell: (cell: any) => cell.getValue() || '—',
            },
            {
                header: 'Contact Phone',
                accessorKey: 'contact_phone',
                cell: (cell: any) => cell.getValue() || '—',
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: (cell: any) => getStatusBadge(cell.getValue()),
            },
            {
                header: 'Actions',
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const unit = cell.row.original;
                    return (
                        <div className="d-flex gap-1">
                            <Button
                                color="info"
                                size="sm"
                                title="View"
                                onClick={() => handleView(unit)}
                            >
                                <Eye size={14} />
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                title="Edit"
                                onClick={() => openEditModal(unit)}
                            >
                                <Edit size={14} />
                            </Button>
                            {unit.status ? (
                                <Button
                                    color="danger"
                                    size="sm"
                                    title="Delete"
                                    onClick={() => openDeleteModal(unit)}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            ) : (
                                <Button
                                    color="success"
                                    size="sm"
                                    title="Restore"
                                    onClick={() => handleRestore(unit)}
                                >
                                    <RefreshCw size={14} />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        [],
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Company Master" pageTitle="Companies" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="card-title mb-0">Companies</h5>
                                <div className="d-flex gap-2">
                                    <FormGroup
                                        check
                                        className="mb-0 d-flex align-items-center"
                                    >
                                        <Label check className="me-2 mb-0">
                                            <Input
                                                type="checkbox"
                                                checked={showInactive}
                                                onChange={() =>
                                                    setShowInactive(!showInactive)
                                                }
                                            />
                                            <span className="ms-1">
                                                Show Inactive
                                            </span>
                                        </Label>
                                    </FormGroup>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => fetchUnits()}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={openAddModal}
                                    >
                                        <Plus size={14} className="me-1" />
                                        Add Company
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <i className="ri-error-warning-line align-middle me-2"></i>
                                        {error}
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading companies...</span>
                                    </div>
                                ) : units.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-building-line display-4 text-muted"></i>
                                        <h5 className="mt-3">No companies found</h5>
                                        <p className="text-muted">
                                            {showInactive
                                                ? 'No companies match your filter criteria.'
                                                : "Click 'Add Company' to create your first company."}
                                        </p>
                                    </div>
                                ) : (
                                    <TableContainer
                                        columns={columns}
                                        data={units}
                                        customPageSize={10}
                                        tableClass="table-striped"
                                        theadClass="table-light"
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ─── Add/Edit Modal ─── */}
            <Modal isOpen={modal} toggle={closeModal} size="lg">
                <ModalHeader toggle={closeModal}>
                    {modalMode === 'add' ? 'Add New Company' : 'Edit Company'}
                </ModalHeader>
                <ModalBody>
                    {formError && (
                        <Alert color="danger" className="mb-3">
                            {formError}
                        </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col md={8}>
                                <FormGroup>
                                    <Label className="form-label-sm">
                                        Company Name *
                                    </Label>
                                    <Input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="e.g. Muneer Ibn Fawaz Engineering Consultancy Private Limited"
                                    />
                                    <small className="text-muted">
                                        Full legal name as it appears on invoices
                                        and payslips.
                                    </small>
                                </FormGroup>
                            </Col>

                            {/* ✅ NEW — Short Name / Code */}
                            <Col md={4}>
                                <FormGroup>
                                    <Label className="form-label-sm">
                                        Short Code
                                    </Label>
                                    <Input
                                        type="text"
                                        name="short_name"
                                        value={formData.short_name}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="MECLR"
                                        maxLength={10}
                                        style={{
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontWeight: 600,
                                        }}
                                    />
                                    <small className="text-muted">
                                        e.g. <code>MECLR</code>, <code>MIFRT</code>,{' '}
                                        <code>MIFER</code> — used for watermark
                                        and short display.
                                    </small>
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">Location</Label>
                                    <Input
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="e.g. Trichy, Chennai, Madurai"
                                        maxLength={100}
                                    />
                                    <small className="text-muted">
                                        City / site where this company operates — helps distinguish companies
                                        with the same name in different locations.
                                    </small>
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <Label className="form-label-sm">
                                        Description
                                    </Label>
                                    <Input
                                        type="textarea"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        rows={2}
                                        placeholder="Enter description (optional)"
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label-sm">
                                        Contact Person
                                    </Label>
                                    <Input
                                        type="text"
                                        name="contact_person"
                                        value={formData.contact_person}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="Enter contact person (optional)"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label-sm">
                                        Contact Phone
                                    </Label>
                                    <Input
                                        type="tel"
                                        name="contact_phone"
                                        value={formData.contact_phone}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                        placeholder="Enter contact phone (optional)"
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={12}>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            name="status"
                                            checked={formData.status}
                                            onChange={handleInputChange}
                                        />
                                        <span className="ms-2">Active</span>
                                    </Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="light"
                        onClick={closeModal}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : modalMode === 'add' ? (
                            'Create'
                        ) : (
                            'Update'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ─── Delete Modal ─── */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <ModalHeader toggle={() => setDeleteModal(false)}>
                    Confirm Delete
                </ModalHeader>
                <ModalBody>
                    <p>
                        Are you sure you want to delete the company{' '}
                        <strong>"{unitToDelete?.name}"</strong>?
                    </p>
                    <Alert color="warning" className="mb-0">
                        <i className="ri-alert-line me-2"></i>
                        This action will deactivate the company. You can restore it
                        later.
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="light"
                        onClick={() => setDeleteModal(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={confirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ─── View Modal ─── */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)}>
                <ModalHeader toggle={() => setViewModal(false)}>
                    Company Details
                </ModalHeader>
                <ModalBody>
                    {viewingUnit && (
                        <div>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Short Code:</strong>
                                </Col>
                                <Col md={7}>
                                    {viewingUnit.short_name ? (
                                        <span
                                            style={{
                                                backgroundColor: '#4b1ac9',
                                                color: '#fff',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                padding: '3px 10px',
                                                borderRadius: 4,
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            {viewingUnit.short_name}
                                        </span>
                                    ) : (
                                        '—'
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Location:</strong>
                                </Col>
                                <Col md={7}>
                                    {viewingUnit.location ? (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                color: '#334155',
                                                fontWeight: 500,
                                            }}
                                        >
                                            <span style={{ color: '#dc2626' }}>📍</span>
                                            {viewingUnit.location}
                                        </span>
                                    ) : (
                                        '—'
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Company Name:</strong>
                                </Col>
                                <Col md={7}>{viewingUnit.name}</Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Description:</strong>
                                </Col>
                                <Col md={7}>{viewingUnit.description || '—'}</Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Contact Person:</strong>
                                </Col>
                                <Col md={7}>
                                    {viewingUnit.contact_person || '—'}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Contact Phone:</strong>
                                </Col>
                                <Col md={7}>
                                    {viewingUnit.contact_phone || '—'}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Status:</strong>
                                </Col>
                                <Col md={7}>
                                    {getStatusBadge(viewingUnit.status)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Created At:</strong>
                                </Col>
                                <Col md={7}>
                                    {formatDate(viewingUnit.created_at)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <strong>Updated At:</strong>
                                </Col>
                                <Col md={7}>
                                    {formatDate(viewingUnit.updated_at)}
                                </Col>
                            </Row>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={() => setViewModal(false)}
                    >
                        Close
                    </Button>
                    {viewingUnit && (
                        <Button
                            color="primary"
                            onClick={() => {
                                setViewModal(false);
                                openEditModal(viewingUnit);
                            }}
                        >
                            <Edit size={14} className="me-1" />
                            Edit
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default UnitMaster;